import { createOperationDescriptor, getRequest, type Disposable } from 'relay-runtime';

import EditorContentUnifiedQuery from './__generated__/EditorContentUnifiedQuery.graphql.ts';
import type {
	EditorContentUnifiedQuery$data,
	EditorContentUnifiedQuery$variables,
} from './__generated__/EditorContentUnifiedQuery.graphql.ts';

type FreshnessEntry = {
	fetchedAt: number;
	retention: Disposable | null;
};

export type EditorContentFetchPolicy = 'cache-first' | 'network-only';

const entries = new Map<string, FreshnessEntry>();

const EDITOR_CONTENT_TTL_MS = 15_000;

// Module state on the server is shared across Bifrost requests, so every function is a no-op there.
function isSSR(): boolean {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	return Boolean(process.env.REACT_SSR);
}

type EditorContentFreshnessKey = Pick<
	EditorContentUnifiedQuery$variables,
	'contentId' | 'draftShareId' | 'isLivePage'
>;

// The edit and live operations of a content select different fields, so each has its own entry.
function getKey({ contentId, draftShareId, isLivePage }: EditorContentFreshnessKey): string {
	return `${contentId}:${draftShareId || ''}:${isLivePage ? 'live' : 'edit'}`;
}

function deleteEntry(key: string): void {
	entries.get(key)?.retention?.dispose();
	entries.delete(key);
}

// Entries of pages the editor never showed (a warm-up without a click) are never cleared otherwise.
function deleteExpired(): void {
	const now = performance.now();
	for (const [key, { fetchedAt }] of entries) {
		if (now - fetchedAt > EDITOR_CONTENT_TTL_MS) {
			deleteEntry(key);
		}
	}
}

// Nothing may read the operation until the editor mounts (e.g. after a hover), so it is retained
// from the start of the fetch; Relay can collect unretained data as soon as it is published.
// Same environment as `query` from query-preloader-tools executes on.
function retain(variables: EditorContentUnifiedQuery$variables): Disposable | null {
	if (isSSR() || !window.GLOBAL_RELAY_ENV) {
		return null;
	}
	return window.GLOBAL_RELAY_ENV.retain(
		createOperationDescriptor(getRequest(EditorContentUnifiedQuery), variables),
	);
}

function markFetched(key: string, retention: Disposable | null): void {
	deleteExpired();
	deleteEntry(key);
	entries.set(key, { fetchedAt: performance.now(), retention });
}

export function isEditorContentFresh(variables: EditorContentFreshnessKey): boolean {
	if (isSSR()) {
		return false;
	}
	const entry = entries.get(getKey(variables));
	return entry !== undefined && performance.now() - entry.fetchedAt <= EDITOR_CONTENT_TTL_MS;
}

/**
 * Runs EditorContentUnifiedQuery with the fetch policy its freshness allows: the store
 * while a network fetch of the same variables is fresh, the network otherwise. A network response
 * with content marks the variables fresh, and the operation stays retained exactly as long.
 */
export function withEditorContentFreshness<
	T extends { data: EditorContentUnifiedQuery$data | null },
>(
	variables: EditorContentUnifiedQuery$variables,
	runQuery: (fetchPolicy: EditorContentFetchPolicy) => Promise<T>,
): Promise<T> {
	const key = getKey(variables);
	// Never fresh on SSR, so the server always fetches.
	const isFresh = isEditorContentFresh(variables);
	// A store read is not new data, so it neither extends the TTL nor the retention.
	const retention = isFresh ? null : retain(variables);

	return runQuery(isFresh ? 'cache-first' : 'network-only').then(
		(result) => {
			if (!isFresh && !isSSR() && result.data?.content) {
				markFetched(key, retention);
			} else {
				retention?.dispose();
			}
			return result;
		},
		(error: unknown) => {
			retention?.dispose();
			throw error;
		},
	);
}

// Clears every draftShareId and route variant of the content.
export function clearEditorContentFetched(contentId: string): void {
	if (isSSR()) {
		return;
	}
	deleteExpired();
	const prefix = `${contentId}:`;
	for (const key of entries.keys()) {
		if (key.startsWith(prefix)) {
			deleteEntry(key);
		}
	}
}

export function clearAllEditorContentFetched(): void {
	for (const key of entries.keys()) {
		deleteEntry(key);
	}
}
